/**
 * LoyaltyCheckoutModal — POS checkout: search by phone → check points → redeem
 */
import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Search, Star, Check, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// Loyalty lookups/redemptions run under the POS staff session
const posAuthHeaders = () => {
  const token = localStorage.getItem('pos_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function LoyaltyCheckoutModal({ open, onClose, orderTotal, onApply }) {
  const [phone, setPhone] = useState('');
  const [customer, setCustomer] = useState(null);
  const [searching, setSearching] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const inputRef = useRef(null);

  // Loyalty settings (defaults; could be fetched from backend)
  const pointsPerDollar = 1;
  const pointsForRedemption = 100;
  const redemptionValue = 5.0;

  const reset = () => {
    setPhone('');
    setCustomer(null);
    setRedeemPoints('');
  };

  const pointsToDollars = (pts) => {
    const sets = Math.floor(pts / pointsForRedemption);
    return sets * redemptionValue;
  };

  const handleSearch = async () => {
    if (!phone.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/loyalty/lookup?phone=${encodeURIComponent(phone)}`, { headers: posAuthHeaders() });
      const data = await res.json();
      if (data.success && data.customer) {
        setCustomer(data.customer);
        // Default to max redeemable
        const maxRedeemable = Math.min(
          pointsToDollars(data.customer.loyalty_points),
          orderTotal
        );
        const maxPoints = Math.floor(maxRedeemable / redemptionValue) * pointsForRedemption;
        setRedeemPoints(String(maxPoints));
      } else {
        toast.info(data.message || 'Customer not found');
        setCustomer(null);
      }
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleRedeem = async () => {
    if (!customer) return;
    const pts = parseInt(redeemPoints) || 0;
    if (pts <= 0 || pts < pointsForRedemption) {
      toast.error(`Minimum ${pointsForRedemption} points required`);
      return;
    }
    if (pts > customer.loyalty_points) {
      toast.error('Insufficient points');
      return;
    }

    const dollarValue = pointsToDollars(pts);
    if (dollarValue > orderTotal) {
      toast.error('Redemption value exceeds order total');
      return;
    }

    setRedeeming(true);
    try {
      const res = await fetch(`${API}/api/loyalty/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...posAuthHeaders() },
        body: JSON.stringify({
          user_id: customer.id,
          points: pts,
          reference: `POS-${Date.now().toString().slice(-8)}`,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${pts} points redeemed (${fmt(dollarValue)} off)`);
        onApply({
          method: 'loyalty',
          points_redeemed: pts,
          dollar_value: dollarValue,
          customer_id: customer.id,
          customer_name: customer.full_name,
          remaining_points: data.remaining_points,
        });
        reset();
        onClose();
      } else {
        toast.error(data.message || 'Redemption failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white" data-testid="loyalty-checkout-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-400" />
            Loyalty Points Payment
          </DialogTitle>
        </DialogHeader>

        {!customer ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Search customer by phone number to check loyalty points</p>

            <div className="flex gap-2">
              <Input
                ref={inputRef}
                placeholder="Enter phone number..."
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-slate-800 border-slate-600"
                data-testid="loyalty-phone-input"
              />
              <Button onClick={handleSearch} disabled={searching} data-testid="loyalty-search-btn">
                <Search className="h-4 w-4 mr-1" />
                {searching ? '...' : 'Find'}
              </Button>
            </div>

            <div className="text-center text-sm text-slate-500 pt-2">
              Order Total: <span className="font-bold text-white">{fmt(orderTotal)}</span>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-400">
              <div>Earn: {pointsPerDollar} point per $1 spent</div>
              <div>Redeem: {pointsForRedemption} points = {fmt(redemptionValue)} off</div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Customer Info */}
            <div className="text-center p-4 bg-slate-800 rounded-lg">
              <div className="text-lg font-medium">{customer.full_name}</div>
              <div className="text-sm text-slate-400">{customer.phone || phone}</div>
              <div className="flex items-center justify-center gap-2 mt-3">
                <Star className="h-6 w-6 text-amber-400 fill-amber-400" />
                <span className="text-3xl font-bold text-amber-400">{customer.loyalty_points}</span>
                <span className="text-sm text-slate-400">points</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Worth up to {fmt(pointsToDollars(customer.loyalty_points))}
              </div>
            </div>

            {/* Redeem */}
            <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Order Total</span>
                <span className="font-medium">{fmt(orderTotal)}</span>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Points to Redeem (multiples of {pointsForRedemption})</label>
                <Input
                  type="number"
                  min={pointsForRedemption}
                  step={pointsForRedemption}
                  max={customer.loyalty_points}
                  value={redeemPoints}
                  onChange={(e) => setRedeemPoints(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-lg font-bold"
                  data-testid="loyalty-redeem-points"
                />
                <div className="text-xs text-emerald-400 mt-1">
                  = {fmt(pointsToDollars(parseInt(redeemPoints) || 0))} discount
                </div>
              </div>
              {pointsToDollars(parseInt(redeemPoints) || 0) < orderTotal && (
                <div className="flex items-center gap-1.5 text-amber-400 text-xs">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Remaining {fmt(orderTotal - pointsToDollars(parseInt(redeemPoints) || 0))} due with another method
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCustomer(null)} className="flex-1">
                Back
              </Button>
              <Button onClick={handleRedeem} disabled={redeeming} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="loyalty-apply-btn">
                <Check className="h-4 w-4 mr-2" />
                {redeeming ? 'Applying...' : `Redeem ${redeemPoints || 0} pts`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
